<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="file"/> {{ $t('clientBotDetect') }}</h1>
        <p>{{ $t('clientBotDetectDesc') }}</p>
      </div>
    </div>

    <div class="row">
      <!-- Tabs loại -->
      <div class="col-12">
        <b-tabs v-model="typeTabIndex" @input="onTypeTab">
          <b-tab :title="$t('all')"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #1976d2"></span> {{ $t('chat') }}
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #43a047"></span> {{ $t('call') }}
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <client-select v-if="!clientId" class="mr-1 mb-1" :model="filterFormModel" attribute="client"
                           :isFilter="true" :place-holder="$t('selectClient')"></client-select>
            <form-select class="mr-1 mb-1" :model="filterFormModel" attribute="bot" :options="botOptions"></form-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" class="mr-1 mb-1"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount > 0">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length > 0">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card"
               :style="{ borderLeftColor: item.type === 'call' ? '#43a047' : '#1976d2' }">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm:ss') }}</div>
              <div class="log-meta">{{ item.createTime | moment('YYYY') }}</div>
            </div>

            <div class="log-card__type">
              <span class="log-type-pill"
                    :style="{ background: item.type === 'call' ? '#43a047' : '#1976d2' }">
                <font-awesome-icon :icon="item.type === 'call' ? 'phone' : 'comment'" class="mr-1"/>
                {{ $t(item.type) }}
              </span>
              <div class="log-meta mt-1" v-if="item.bot">{{ item.bot.name }}</div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1">
                <font-awesome-icon icon="user" class="text-muted mr-1"/>
                <strong v-if="item.customer">{{ item.customer.name }}</strong>
                <span v-else class="text-muted">—</span>
                <span v-if="item.client" class="log-meta ml-3">
                  <font-awesome-icon icon="building" class="mr-1"/>{{ item.client.domain }}
                </span>
              </div>
              <div class="log-row-2 log-content">{{ item.content || '—' }}</div>
              <div class="log-row-3" v-if="item.account">
                <font-awesome-icon icon="hashtag" class="text-muted mr-1"/>
                <span class="log-meta">{{ item.account.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="items.length === 0 && !loading">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        {{ $t('notFound') }}
      </div>

      <div class="col-12" v-if="itemCount > filterFormModel.pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import List from "@/mixins/List";
import TimeRangePicker from "@/components/TimeRangePicker.vue";
import ClientSelect from "@/components/ClientSelect.vue";

export default {
  components: { ClientSelect, TimeRangePicker },
  mixins: [List],
  props: { clientId: { type: String, default: null } },
  data() {
    return {
      service: '/client-bot-detect',
      autoLoad: true,
      typeTabIndex: 0,
      defaultFilterFormModel: {
        client: this.clientId || null,
        type: null,
        bot: null,
      }
    }
  },
  computed: {
    botOptions() {
      const seen = new Set();
      const uniqueBots = this.items
        .filter(item => item.bot && !seen.has(item.bot._id) && seen.add(item.bot._id))
        .map(item => ({ value: item.bot._id, text: item.bot.name }));
      return [{ value: null, text: this.$t('selectBot') }, ...uniqueBots];
    }
  },
  methods: {
    onTypeTab(idx) {
      this.filterFormModel.type = idx === 0 ? null : (idx === 1 ? 'chat' : 'call');
      this.filterFormModel.page = 1;
      this.load();
    }
  },
  i18n: {
    messages: {
      en: {
        clientBotDetect: 'Conversation monitoring',
        clientBotDetectDesc: 'Suspicious chat/call detection records.',
        all: 'All',
        bot: 'Group',
        chat: 'Chat',
        call: 'Call',
        selectBot: 'Select group',
        notFound: 'No records found',
      },
      vi: {
        clientBotDetect: 'Giám sát giao tiếp',
        clientBotDetectDesc: 'Bản ghi giám sát chat/cuộc gọi nghi vấn.',
        all: 'Tất cả',
        bot: 'Nhóm',
        chat: 'Chat',
        call: 'Gọi',
        selectBot: 'Chọn nhóm',
        notFound: 'Không tìm thấy bản ghi nào',
      }
    }
  }
}
</script>

<style scoped>
.log-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.log-list { display: flex; flex-direction: column; gap: 8px; }
.log-card {
  display: flex; align-items: flex-start; background: #fff;
  border: 1px solid #e8eaed; border-left: 4px solid #888; border-radius: 6px;
  padding: 12px 16px; gap: 16px; transition: all 0.15s;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.log-card__time { min-width: 110px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }
.log-card__type { min-width: 130px; flex-shrink: 0; }
.log-type-pill {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  color: #fff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; white-space: nowrap;
}
.log-card__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.log-row-1 { font-size: 14px; color: #222; }
.log-row-2 { font-size: 13px; color: #444; word-break: break-word; }
.log-content { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.log-row-3 { font-size: 12px; }
</style>
